import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { useNotification } from '../../contexts/NotificationContext';
import { authFetch } from '../../utils/authFetch';
import Footer from '../../components/Footer';

const DIAS = [
    { value: 1, label: 'Segunda', short: 'Seg' },
    { value: 2, label: 'Terça',   short: 'Ter' },
    { value: 3, label: 'Quarta',  short: 'Qua' },
    { value: 4, label: 'Quinta',  short: 'Qui' },
    { value: 5, label: 'Sexta',   short: 'Sex' },
];

const SHIFT_LABELS = { 0: 'Manhã', 1: 'Tarde' };

// ─── utilidades de data ───────────────────────────────────────────────────────
const toISODate = d => d.toISOString().split('T')[0];

const addDays = (dateStr, days) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return toISODate(d);
};

const getMondayOfWeek = (offset = 0) => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff + offset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
};

// ─── utilidades de tempo ──────────────────────────────────────────────────────
const fmt = t => (t ? t.substring(0, 5) : '');
const formatDisplay = dateStr => { if (!dateStr) return ''; const [, m, d] = dateStr.split('-'); return `${d}/${m}`; };

const toMin = t => {
    if (!t) return 0;
    const [h, m] = t.substring(0, 5).split(':').map(Number);
    return h * 60 + (m || 0);
};
const minToStr = mins => {
    if (mins <= 0) return '0h';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h${m}m` : `${h}h`;
};
const overlapMin = (aS, aE, bS, bE) =>
    Math.max(0, Math.min(toMin(aE), toMin(bE)) - Math.max(toMin(aS), toMin(bS)));

// Gera slots de 1h dentro do período
const getHourlySlots = (pStart, pEnd) => {
    if (!pStart || !pEnd) return [];
    const slots = [];
    let cur = toMin(pStart);
    const end = toMin(pEnd);
    while (cur < end) {
        const next = Math.min(cur + 60, end);
        const sS = `${String(Math.floor(cur / 60)).padStart(2, '0')}:${String(cur % 60).padStart(2, '0')}`;
        const sE = `${String(Math.floor(next / 60)).padStart(2, '0')}:${String(next % 60).padStart(2, '0')}`;
        slots.push({ start: sS, end: sE, minutes: next - cur });
        cur = next;
    }
    return slots;
};

// Status de um slot: 'free' | 'blocked' | 'occupied'
const getSlotStatus = (slotStart, slotEnd, dateStr, shift, blocks, appointments) => {
    for (const b of blocks) {
        if (dateStr < b.startDate || dateStr > b.endDate) continue;
        if (b.shift !== 2 && b.shift !== shift) continue;
        const bS = b.startTime ? fmt(b.startTime) : slotStart;
        const bE = b.endTime   ? fmt(b.endTime)   : slotEnd;
        if (overlapMin(slotStart, slotEnd, bS, bE) > 0) return { status: 'blocked', block: b };
    }
    for (const a of appointments) {
        if (a.absenceType === 1 || a.absenceType === 2) continue;
        if (a.startDatetime?.substring(0, 10) !== dateStr) continue;
        const aS = a.startDatetime?.substring(11, 16);
        const aE = a.endDatetime?.substring(11, 16);
        if (aS && aE && overlapMin(slotStart, slotEnd, aS, aE) > 0) return { status: 'occupied', appt: a };
    }
    return { status: 'free' };
};

// Minutos livres de um período (para o resumo)
const calcFreeMin = (period, dateStr, blocks, appointments) => {
    const pS = period.startTime ? fmt(period.startTime) : null;
    const pE = period.endTime   ? fmt(period.endTime)   : null;
    if (!pS || !pE) return null;
    return getHourlySlots(pS, pE).reduce((acc, slot) => {
        const { status } = getSlotStatus(slot.start, slot.end, dateStr, period.shift, blocks, appointments);
        return acc + (status === 'free' ? slot.minutes : 0);
    }, 0);
};

// ─── componente de slot individual (modo Por Terapeuta) ───────────────────────
const SlotRow = ({ slot, status, block, appt, isPast }) => {
    if (status === 'blocked') return (
        <div className="rounded px-2 py-1 mb-1" style={{ background: '#fee2e2' }} title={block?.reason || 'Bloqueado'}>
            <div className="d-flex align-items-center gap-1">
                <i className="bi bi-lock-fill text-danger" style={{ fontSize: '0.6rem', flexShrink: 0 }}></i>
                <span className="text-danger" style={{ fontSize: '0.65rem', fontWeight: 600 }}>{slot.start}–{slot.end}</span>
            </div>
            {block?.reason && (
                <div className="text-danger" style={{ fontSize: '0.55rem', opacity: 0.8, marginTop: 1 }}>{block.reason}</div>
            )}
        </div>
    );
    if (status === 'occupied') {
        const nome = appt?.patient?.name?.split(' ')[0] || appt?.title || 'Agendado';
        return (
            <div className="rounded px-2 py-1 mb-1" style={{ background: '#dbeafe' }}>
                <div className="d-flex align-items-center gap-1">
                    <i className="bi bi-person-fill text-primary" style={{ fontSize: '0.6rem', flexShrink: 0 }}></i>
                    <span className="text-primary" style={{ fontSize: '0.65rem', fontWeight: 600 }}>{slot.start}–{slot.end}</span>
                </div>
                <div className="text-secondary" style={{ fontSize: '0.55rem', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</div>
            </div>
        );
    }
    return (
        <div className="rounded px-2 py-1 mb-1 d-flex align-items-center gap-1" style={{ background: isPast ? '#f3f4f6' : '#dcfce7' }}>
            {!isPast && <i className="bi bi-check2 text-success" style={{ fontSize: '0.6rem', flexShrink: 0 }}></i>}
            <span style={{ fontSize: '0.65rem', fontWeight: 600, color: isPast ? '#9ca3af' : '#16a34a' }}>{slot.start}–{slot.end}</span>
        </div>
    );
};

// ─── tooltip simples ─────────────────────────────────────────────────────────
const DotTooltip = ({ color, name }) => {
    const [visible, setVisible] = useState(false);
    return (
        <span
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            <span style={{
                width: 16, height: 16,
                borderRadius: '50%',
                background: color || '#16a34a',
                display: 'inline-block',
                flexShrink: 0,
                border: '2px solid #fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                cursor: 'default',
            }} />
            {visible && (
                <span style={{
                    position: 'absolute',
                    bottom: '130%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(17,24,39,0.88)',
                    color: '#fff',
                    padding: '3px 8px',
                    borderRadius: 5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    zIndex: 99,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                }}>
                    {name}
                </span>
            )}
        </span>
    );
};

// ─── célula de disponibilidade (modo Visão Geral) ─────────────────────────────
const CELL_BORDER = '1px solid #e5e7eb';

const OverviewCell = ({ livre, hasSchedule }) => {
    if (!hasSchedule) {
        return <td style={{ borderLeft: CELL_BORDER, borderBottom: CELL_BORDER, background: '#fff' }}></td>;
    }
    return (
        <td style={{
            borderLeft: CELL_BORDER,
            borderBottom: CELL_BORDER,
            background: livre.length > 0 ? '#dcfce7' : '#f9fafb',
            verticalAlign: 'middle',
            padding: '5px 10px',
        }}>
            {livre.length > 0 ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {livre.map(t => <DotTooltip key={t.id} color={t.color} name={t.name} />)}
                </div>
            ) : (
                <span style={{ color: '#d1d5db', fontSize: '0.65rem' }}>—</span>
            )}
        </td>
    );
};

// ─── página principal ─────────────────────────────────────────────────────────
export default function Disponibilidade() {
    const { userAPIData } = useUserAPI();
    const { showNotification } = useNotification();

    const isAdmin = Number(userAPIData?.userRoleId) === 1;
    const today   = toISODate(new Date());

    const [viewMode, setViewMode] = useState('overview');

    // semana — compartilhada pelos dois modos
    const [weekOffset, setWeekOffset] = useState(0);
    const monday   = getMondayOfWeek(weekOffset);
    const weekDays = DIAS.map((dia, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return { ...dia, date: toISODate(d) };
    });
    const weekStart = weekDays[0].date;
    const weekEnd   = weekDays[4].date;
    const weekLabel = (() => {
        if (weekOffset ===  0) return `Esta semana (${formatDisplay(weekStart)} – ${formatDisplay(weekEnd)})`;
        if (weekOffset ===  1) return `Próxima semana (${formatDisplay(weekStart)} – ${formatDisplay(weekEnd)})`;
        if (weekOffset === -1) return `Semana passada (${formatDisplay(weekStart)} – ${formatDisplay(weekEnd)})`;
        return `${formatDisplay(weekStart)} – ${formatDisplay(weekEnd)}`;
    })();

    // ── modo Por Terapeuta ──────────────────────────────────────────────────
    const [terapeutas, setTerapeutas]                     = useState([]);
    const [terapeutaSelecionado, setTerapeutaSelecionado] = useState(null);
    const [loadingTerapeutas, setLoadingTerapeutas]       = useState(false);
    const [schedule, setSchedule]                         = useState([]);
    const [blocks, setBlocks]                             = useState([]);
    const [appointments, setAppointments]                 = useState([]);
    const [loadingData, setLoadingData]                   = useState(false);

    // ── modo Visão Geral ────────────────────────────────────────────────────
    const [allSchedules, setAllSchedules]     = useState([]);
    const [allBlocks, setAllBlocks]           = useState([]);
    const [overviewAppts, setOverviewAppts]   = useState([]);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [loadingOverviewAppts, setLoadingOverviewAppts] = useState(false);

    // ── carregamentos ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isAdmin) return;
        setLoadingTerapeutas(true);
        authFetch('therapist')
            .then(r => r.json())
            .then(data => setTerapeutas(data.filter(t => !t.disabled)))
            .catch(() => showNotification('danger', 'Erro ao carregar terapeutas'))
            .finally(() => setLoadingTerapeutas(false));
    }, [isAdmin]);

    useEffect(() => {
        if (isAdmin || !userAPIData?.id) return;
        authFetch(`therapist/by-user/${userAPIData.id}`)
            .then(r => r.ok ? r.json() : null)
            .then(t => { if (t) { setTerapeutaSelecionado(t); setViewMode('therapist'); } })
            .catch(() => showNotification('danger', 'Erro ao identificar terapeuta'));
    }, [isAdmin, userAPIData?.id]);

    useEffect(() => {
        if (!terapeutaSelecionado) { setSchedule([]); setBlocks([]); return; }
        const id = terapeutaSelecionado.id;
        Promise.all([
            authFetch(`therapist-schedule/${id}`).then(r => r.json()),
            authFetch(`therapist-schedule/${id}/blocks`).then(r => r.json()),
        ])
            .then(([sched, blks]) => { setSchedule(sched); setBlocks(blks); })
            .catch(() => showNotification('danger', 'Erro ao carregar escala'));
    }, [terapeutaSelecionado]);

    // Agendamentos da semana — modo Por Terapeuta
    // endDate + 1: o SQL BETWEEN cast para meia-noite, excluindo o dia inteiro do endDate
    const fetchWeekAppts = useCallback(() => {
        if (!terapeutaSelecionado) { setAppointments([]); return; }
        setLoadingData(true);
        authFetch(`appointment?therapistId=${terapeutaSelecionado.id}&startDate=${weekStart}&endDate=${addDays(weekEnd, 1)}`)
            .then(r => r.json())
            .then(data => setAppointments(Array.isArray(data) ? data : []))
            .catch(() => showNotification('danger', 'Erro ao carregar agendamentos'))
            .finally(() => setLoadingData(false));
    }, [terapeutaSelecionado, weekStart, weekEnd]);

    useEffect(() => { if (viewMode === 'therapist') fetchWeekAppts(); }, [viewMode, fetchWeekAppts]);

    // Escala geral + bloqueios — modo Visão Geral (carrega uma vez)
    useEffect(() => {
        if (viewMode !== 'overview') return;
        setLoadingOverview(true);
        Promise.all([
            authFetch('therapist-schedule').then(r => r.json()),
            authFetch('therapist-schedule/blocks/upcoming').then(r => r.json()),
        ])
            .then(([scheds, blks]) => { setAllSchedules(scheds); setAllBlocks(blks); })
            .catch(() => showNotification('danger', 'Erro ao carregar escala geral'))
            .finally(() => setLoadingOverview(false));
    }, [viewMode]);

    // Agendamentos da semana — modo Visão Geral
    useEffect(() => {
        if (viewMode !== 'overview') return;
        setLoadingOverviewAppts(true);
        authFetch(`appointment?startDate=${weekStart}&endDate=${addDays(weekEnd, 1)}`)
            .then(r => r.json())
            .then(data => setOverviewAppts(Array.isArray(data) ? data : []))
            .catch(() => showNotification('danger', 'Erro ao carregar agendamentos'))
            .finally(() => setLoadingOverviewAppts(false));
    }, [viewMode, weekStart, weekEnd]);

    // ── cálculo Visão Geral: para cada (shift, slotHorário), quem está livre em cada dia ──
    const overviewData = useMemo(() => {
        if (viewMode !== 'overview' || !terapeutas.length || !allSchedules.length) return null;

        // Coleta todos os slots únicos (shift + horário) que existem em qualquer dia da semana
        const allSlotKeys = new Set();
        for (const dia of weekDays) {
            for (const t of terapeutas) {
                allSchedules
                    .filter(s => s.therapistId === t.id && s.dayOfWeek === dia.value)
                    .forEach(p => {
                        const pS = p.startTime ? fmt(p.startTime) : null;
                        const pE = p.endTime   ? fmt(p.endTime)   : null;
                        if (!pS || !pE) return;
                        getHourlySlots(pS, pE).forEach(slot =>
                            allSlotKeys.add(`${p.shift}|${slot.start}|${slot.end}`)
                        );
                    });
            }
        }

        // Ordena: shift asc, depois hora asc
        const sortedSlots = [...allSlotKeys]
            .map(k => { const [shift, start, end] = k.split('|'); return { shift: Number(shift), start, end }; })
            .sort((a, b) => a.shift - b.shift || toMin(a.start) - toMin(b.start));

        // Para cada slot × dia: lista de terapeutas livres
        const matrix = sortedSlots.map(slot => {
            const days = weekDays.map(dia => {
                // Verifica se ao menos uma terapeuta tem escala para este slot neste dia
                const hasSchedule = terapeutas.some(t =>
                    allSchedules.some(s =>
                        s.therapistId === t.id &&
                        s.dayOfWeek === dia.value &&
                        s.shift === slot.shift &&
                        s.startTime && s.endTime &&
                        toMin(fmt(s.startTime)) <= toMin(slot.start) &&
                        toMin(fmt(s.endTime)) >= toMin(slot.end)
                    )
                );

                if (!hasSchedule) return { hasSchedule: false, livre: [] };

                const livre = terapeutas.filter(t => {
                    const covers = allSchedules.some(s =>
                        s.therapistId === t.id &&
                        s.dayOfWeek === dia.value &&
                        s.shift === slot.shift &&
                        s.startTime && s.endTime &&
                        toMin(fmt(s.startTime)) <= toMin(slot.start) &&
                        toMin(fmt(s.endTime)) >= toMin(slot.end)
                    );
                    if (!covers) return false;
                    if (t.vacationDate && t.vacationEndDate &&
                        dia.date >= t.vacationDate && dia.date <= t.vacationEndDate) return false;
                    const tBlocks = allBlocks.filter(b => b.therapistId === t.id);
                    const tAppts  = overviewAppts.filter(a => a.therapistId === t.id);
                    const { status } = getSlotStatus(slot.start, slot.end, dia.date, slot.shift, tBlocks, tAppts);
                    return status === 'free';
                });

                return { hasSchedule: true, livre };
            });

            return { slot, days };
        });

        // Separar por turno
        return {
            manha: matrix.filter(r => r.slot.shift === 0),
            tarde: matrix.filter(r => r.slot.shift === 1),
        };
    }, [viewMode, terapeutas, allSchedules, allBlocks, overviewAppts, weekDays]);

    // ── total de minutos livres (modo Por Terapeuta) ─────────────────────────
    const totalFreeMin = useMemo(() =>
        weekDays.reduce((acc, dia) => {
            if (dia.date < today) return acc;
            return acc + schedule
                .filter(s => s.dayOfWeek === dia.value)
                .reduce((a, p) => a + (calcFreeMin(p, dia.date, blocks, appointments) ?? 0), 0);
        }, 0),
    [weekDays, schedule, blocks, appointments, today]);

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="content">
            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <Link to="/Calendario" className="text-secondary text-xs">
                        <i className="bi bi-arrow-left me-1"></i>Calendário
                    </Link>
                    <h3 className="font-weight-bolder mb-0 mt-1">Disponibilidade de Agenda</h3>
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {isAdmin && (
                        <div style={{ minWidth: 220 }}>
                            <select className="form-select form-select-sm"
                                value={terapeutaSelecionado?.id ?? ''}
                                onChange={e => {
                                    const t = terapeutas.find(x => String(x.id) === e.target.value) || null;
                                    setTerapeutaSelecionado(t);
                                    setViewMode(t ? 'therapist' : 'overview');
                                }}
                                disabled={loadingTerapeutas}>
                                <option value="">— Todos os terapeutas —</option>
                                {terapeutas.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Navegação de semana — compartilhada pelos dois modos */}
                    <div className="d-flex align-items-center gap-1">
                        <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setWeekOffset(w => w - 1)}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="text-sm px-2" style={{ whiteSpace: 'nowrap' }}>{weekLabel}</span>
                        <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setWeekOffset(w => w + 1)}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                        {weekOffset !== 0 && (
                            <button className="btn btn-sm btn-outline-secondary py-0 px-2 ms-1" onClick={() => setWeekOffset(0)}>
                                Hoje
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Legenda ── */}
            <div className="d-flex gap-3 mb-3 flex-wrap">
                {(viewMode === 'overview' ? [
                    { bg: '#dcfce7', border: '#86efac', label: 'Disponível' },
                    { bg: '#f3f4f6', border: '#d1d5db', label: 'Sem disponibilidade' },
                ] : [
                    { bg: '#dcfce7', border: '#86efac', label: 'Livre' },
                    { bg: '#dbeafe', border: '#93c5fd', label: 'Agendado' },
                    { bg: '#fee2e2', border: '#fca5a5', label: 'Bloqueado' },
                    { bg: '#fef9c3', border: '#fde68a', label: 'Férias' },
                    { bg: '#f3f4f6', border: '#d1d5db', label: 'Não trabalha' },
                ]).map(({ bg, border, label }) => (
                    <div key={label} className="d-flex align-items-center gap-1">
                        <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, display: 'inline-block' }}></span>
                        <span className="text-xs text-secondary">{label}</span>
                    </div>
                ))}
            </div>

            {/* ══════════════════ MODO: POR TERAPEUTA ══════════════════ */}
            {viewMode === 'therapist' && (
                !terapeutaSelecionado ? (
                    <div className="card">
                        <div className="card-body p-4 text-center text-secondary">
                            {!isAdmin ? 'Carregando sua escala...' : 'Selecione um terapeuta para visualizar a disponibilidade.'}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            {terapeutaSelecionado.color && (
                                <span className="rounded-circle d-inline-block"
                                    style={{ width: 12, height: 12, background: terapeutaSelecionado.color }}></span>
                            )}
                            <span className="text-sm font-weight-bold">{terapeutaSelecionado.name}</span>
                        </div>

                        <div className="card mb-3" style={{ position: 'relative' }}>
                            {loadingData && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                                    zIndex: 10, borderRadius: 'inherit',
                                }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                    </div>
                                </div>
                            )}
                            <div className="card-body p-3">
                                <div className="row g-2">
                                    {weekDays.map(dia => {
                                        const isPast     = dia.date < today;
                                        const isToday    = dia.date === today;
                                        const isOnVacation = !!(
                                            terapeutaSelecionado.vacationDate &&
                                            terapeutaSelecionado.vacationEndDate &&
                                            dia.date >= terapeutaSelecionado.vacationDate &&
                                            dia.date <= terapeutaSelecionado.vacationEndDate
                                        );
                                        const dayPeriods = schedule
                                            .filter(s => s.dayOfWeek === dia.value)
                                            .sort((a, b) => a.shift - b.shift);

                                        return (
                                            <div key={dia.value} className="col" style={{ minWidth: 120 }}>
                                                <div className={`border rounded p-2 h-100 ${isToday ? 'border-primary' : ''}`}
                                                    style={{ opacity: isPast ? 0.55 : 1 }}>
                                                    <div className="text-center mb-2">
                                                        <div className={`font-weight-bold ${isToday ? 'text-primary' : 'text-secondary'}`}
                                                            style={{ textTransform: 'uppercase', fontSize: '0.6rem' }}>
                                                            {dia.short}
                                                        </div>
                                                        <div className={`font-weight-bold ${isToday ? 'text-primary' : ''}`}
                                                            style={{ fontSize: '0.8rem' }}>
                                                            {formatDisplay(dia.date)}
                                                        </div>
                                                        {isPast && !isToday && (
                                                            <span className="text-secondary" style={{ fontSize: '0.55rem' }}>passado</span>
                                                        )}
                                                    </div>

                                                    {isOnVacation ? (
                                                        <div className="rounded px-2 py-1 mb-1 text-center"
                                                            style={{ background: '#fef9c3', border: '1px solid #fde68a' }}>
                                                            <i className="bi bi-sun" style={{ fontSize: '0.65rem', color: '#92400e' }}></i>
                                                            <span style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 600, marginLeft: 3 }}>Férias</span>
                                                        </div>
                                                    ) : dayPeriods.length === 0 ? (
                                                        <div className="text-center text-secondary" style={{ fontSize: '0.7rem' }}>—</div>
                                                    ) : (
                                                        dayPeriods.map((period, pi) => {
                                                            const pStart = period.startTime ? fmt(period.startTime) : null;
                                                            const pEnd   = period.endTime   ? fmt(period.endTime)   : null;
                                                            const hourlySlots = getHourlySlots(pStart, pEnd);
                                                            return (
                                                                <div key={pi} className={`mb-2 ${pi > 0 ? 'pt-2 border-top' : ''}`}>
                                                                    <div className="d-flex align-items-center justify-content-between mb-1 gap-1">
                                                                        <span className="badge" style={{
                                                                            background: period.shift === 0 ? '#dbeafe' : '#fef9c3',
                                                                            color:      period.shift === 0 ? '#1e40af' : '#854d0e',
                                                                            fontSize: '0.6rem',
                                                                        }}>
                                                                            {SHIFT_LABELS[period.shift]}
                                                                        </span>
                                                                        {pStart && pEnd && (
                                                                            <span className="text-secondary" style={{ fontSize: '0.6rem' }}>
                                                                                {pStart}–{pEnd}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {hourlySlots.length > 0 ? (
                                                                        hourlySlots.map(slot => {
                                                                            const { status, block, appt } = getSlotStatus(
                                                                                slot.start, slot.end, dia.date, period.shift, blocks, appointments
                                                                            );
                                                                            return <SlotRow key={slot.start} slot={slot} status={status} block={block} appt={appt} isPast={isPast} />;
                                                                        })
                                                                    ) : (
                                                                        <div className="rounded px-2 py-1 mb-1"
                                                                            style={{ background: isPast ? '#f3f4f6' : '#dcfce7', fontSize: '0.65rem' }}>
                                                                            <span style={{ color: isPast ? '#9ca3af' : '#16a34a', fontWeight: 600 }}>Disponível</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                    <div>
                                        <span className="text-secondary text-xs text-uppercase font-weight-bolder">Disponível esta semana</span>
                                        <div className="d-flex align-items-baseline gap-2 mt-1">
                                            <span className="h4 mb-0 text-success font-weight-bolder">{minToStr(totalFreeMin)}</span>
                                            <span className="text-xs text-secondary">de tempo livre (dias futuros)</span>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-3 flex-wrap">
                                        {weekDays.filter(d => d.date >= today).map(dia => {
                                            const dayFree  = schedule
                                                .filter(s => s.dayOfWeek === dia.value)
                                                .reduce((a, p) => a + (calcFreeMin(p, dia.date, blocks, appointments) ?? 0), 0);
                                            const hasSched = schedule.some(s => s.dayOfWeek === dia.value);
                                            return (
                                                <div key={dia.value} className="text-center">
                                                    <div className="text-secondary" style={{ fontSize: '0.6rem' }}>{dia.short}</div>
                                                    <div className={`font-weight-bold ${dayFree > 0 ? 'text-success' : 'text-secondary'}`}
                                                        style={{ fontSize: '0.8rem' }}>
                                                        {!hasSched ? '—' : minToStr(dayFree)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            )}

            {/* ══════════════════ MODO: VISÃO GERAL ══════════════════ */}
            {viewMode === 'overview' && (
                <div className="card mb-4">
                    <div className="card-body p-3" style={{ position: 'relative' }}>
                        {(loadingOverview || loadingOverviewAppts) && (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                zIndex: 10, borderRadius: 'inherit', minHeight: 80,
                            }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        )}
                        {!loadingOverview && !loadingOverviewAppts && (
                            (!overviewData || (overviewData.manha.length === 0 && overviewData.tarde.length === 0)) ? (
                            <div className="text-center py-4 text-secondary">
                                Nenhuma escala cadastrada para esta semana.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table mb-0" style={{ borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                                            <th style={{
                                                width: 95, fontSize: '0.7rem', color: '#6b7280',
                                                fontWeight: 700, padding: '6px 10px 10px',
                                                borderBottom: '2px solid #d1d5db',
                                            }}>
                                                Horário
                                            </th>
                                            {weekDays.map(dia => {
                                                const isToday = dia.date === today;
                                                const isPast  = dia.date < today;
                                                return (
                                                    <th key={dia.value} className="text-center"
                                                        style={{
                                                            fontSize: '0.72rem', fontWeight: 700,
                                                            padding: '6px 12px 10px',
                                                            borderLeft: '2px solid #d1d5db',
                                                            borderBottom: '2px solid #d1d5db',
                                                            color: isToday ? '#3b82f6' : isPast ? '#9ca3af' : '#374151',
                                                            background: isToday ? '#eff6ff' : 'transparent',
                                                            minWidth: 80,
                                                        }}>
                                                        <div style={{ textTransform: 'uppercase', fontSize: '0.58rem', letterSpacing: '0.04em', marginBottom: 2, opacity: 0.7 }}>
                                                            {dia.short}
                                                        </div>
                                                        <div style={{ fontWeight: 800 }}>{formatDisplay(dia.date)}</div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {/* Seção Manhã */}
                                        {overviewData.manha.length > 0 && (
                                            <>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td colSpan={6} style={{ padding: '8px 10px 4px', borderBottom: '1px solid #e5e7eb' }}>
                                                        <span className="badge" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.68rem' }}>
                                                            Manhã
                                                        </span>
                                                    </td>
                                                </tr>
                                                {overviewData.manha.map(({ slot, days }) => (
                                                    <tr key={`m-${slot.start}`}>
                                                        <td style={{
                                                            fontSize: '0.75rem', fontWeight: 600, color: '#374151',
                                                            verticalAlign: 'middle', padding: '5px 10px',
                                                            borderBottom: '1px solid #f3f4f6',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {slot.start}–{slot.end}
                                                        </td>
                                                        {days.map((cell, i) => (
                                                            <OverviewCell key={i} livre={cell.livre} hasSchedule={cell.hasSchedule} />
                                                        ))}
                                                    </tr>
                                                ))}
                                            </>
                                        )}

                                        {/* Seção Tarde */}
                                        {overviewData.tarde.length > 0 && (
                                            <>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td colSpan={6} style={{ padding: '10px 10px 4px', borderBottom: '1px solid #e5e7eb', borderTop: '2px solid #e5e7eb' }}>
                                                        <span className="badge" style={{ background: '#fef9c3', color: '#854d0e', fontSize: '0.68rem' }}>
                                                            Tarde
                                                        </span>
                                                    </td>
                                                </tr>
                                                {overviewData.tarde.map(({ slot, days }) => (
                                                    <tr key={`t-${slot.start}`}>
                                                        <td style={{
                                                            fontSize: '0.75rem', fontWeight: 600, color: '#374151',
                                                            verticalAlign: 'middle', padding: '5px 10px',
                                                            borderBottom: '1px solid #f3f4f6',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {slot.start}–{slot.end}
                                                        </td>
                                                        {days.map((cell, i) => (
                                                            <OverviewCell key={i} livre={cell.livre} hasSchedule={cell.hasSchedule} />
                                                        ))}
                                                    </tr>
                                                ))}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
